import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  BookOpen, ArrowLeft, Clock, MapPin, User, Star, Users, 
  CheckCircle, Lightbulb, Target, GraduationCap, UserCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// Basic School Subject Data
const basicSchoolSubjectsData = {
  mathematics: {
    name: "Mathematics",
    color: "from-blue-500 to-cyan-400",
    icon: "🔢",
    room: "Block A, Room 101",
    schedule: "Mon-Fri 8:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building strong mathematical foundations through problem-solving, critical thinking, and practical applications.",
    fullDescription: "The Mathematics program at Young Christian Preparatory School develops strong numerical and analytical skills. Students learn arithmetic, geometry, algebra basics, and problem-solving strategies that prepare them for higher-level mathematics.",
    objectives: [
      "Master arithmetic operations and number sense",
      "Understand basic geometry and measurement",
      "Develop algebraic thinking and pattern recognition",
      "Apply mathematics to real-world problems",
      "Build logical reasoning and critical thinking skills",
      "Prepare for JHS mathematics curriculum"
    ],
    activities: [
      "Problem-solving workshops",
      "Math games and competitions",
      "Hands-on measurement activities",
      "Group projects and presentations",
      "Mental math exercises",
      "Real-world application projects"
    ],
    materials: ["Textbooks", "Workbooks", "Calculators", "Geometry sets", "Math manipulatives"],
    rating: 4.8,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "https://www.khanacademy.org/" },
      { name: "Resource 2", url: "https://www.education.com" },
      { name: "Resource 3", url: "https://www.ixl.com/math/grade-1" },
      { name: "Resource 4", url: "https://www.twinkl.com/twinkl/charts" },
      { name: "Resource 5", url: "https://www.worksheetfun.com/preschool-worksheets/" },
      { name: "Resource 6", url: "#math-6" },
      { name: "Resource 7", url: "#math-7" },
      { name: "Resource 8", url: "#math-8" },
      { name: "Resource 9", url: "#math-9" },
      { name: "Resource 10", url: "#math-10" },
      { name: "Homework 1", url: "#math-hw-1" },
      { name: "Homework 2", url: "#math-hw-2" },
      { name: "Homework 3", url: "#math-hw-3" },
      { name: "Homework 4", url: "#math-hw-4" },
      { name: "Homework 5", url: "#math-hw-5" },
      { name: "Homework 6", url: "#math-hw-6" },
      { name: "Homework 7", url: "#math-hw-7" },
      { name: "Homework 8", url: "#math-hw-8" },
      { name: "Homework 9", url: "#math-hw-9" },
      { name: "Homework 10", url: "#math-hw-10" },
    ]
  },
  "english-language": {
    name: "English Language",
    color: "from-blue-500 to-cyan-400",
    icon: "📖",
    room: "Block A, Room 102",
    schedule: "Mon-Fri 9:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Developing reading, writing, speaking, and listening skills for effective communication.",
    fullDescription: "Our English Language program builds comprehensive literacy skills. Students develop proficiency in reading comprehension, creative and academic writing, grammar, vocabulary, and oral communication.",
    objectives: [
      "Develop fluent reading and comprehension skills",
      "Master grammar and sentence structure",
      "Build vocabulary through reading and context",
      "Write clearly and creatively",
      "Speak confidently in various contexts",
      "Appreciate literature and creative expression"
    ],
    activities: [
      "Reading circles and book clubs",
      "Creative writing workshops",
      "Spelling bees and vocabulary games",
      "Drama and public speaking",
      "Essay writing practice",
      "Comprehension exercises"
    ],
    materials: ["English textbooks", "Reading books", "Dictionaries", "Writing journals", "Grammar workbooks"],
    rating: 4.9,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "https://readingeggs.com/" },
      { name: "Resource 2", url: "https://www.reading.com/" },
      { name: "Resource 3", url: "#english-3" },
      { name: "Resource 4", url: "#english-4" },
      { name: "Resource 5", url: "#english-5" },
      { name: "Resource 6", url: "#english-6" },
      { name: "Resource 7", url: "#english-7" },
      { name: "Resource 8", url: "#english-8" },
      { name: "Resource 9", url: "#english-9" },
      { name: "Resource 10", url: "#english-10" },
      { name: "Homework 1", url: "#english-hw-1" },
      { name: "Homework 2", url: "#english-hw-2" },
      { name: "Homework 3", url: "#english-hw-3" },
      { name: "Homework 4", url: "#english-hw-4" },
      { name: "Homework 5", url: "#english-hw-5" },
      { name: "Homework 6", url: "#english-hw-6" },
      { name: "Homework 7", url: "#english-hw-7" },
      { name: "Homework 8", url: "#english-hw-8" },
      { name: "Homework 9", url: "#english-hw-9" },
      { name: "Homework 10", url: "#english-hw-10" },
    ]
  },
  "natural-science": {
    name: "Natural Science",
    color: "from-blue-500 to-cyan-400",
    icon: "🔬",
    room: "Science Lab",
    schedule: "Mon/Wed/Fri 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Exploring the natural world through observation, experimentation, and scientific inquiry.",
    fullDescription: "The Natural Science program introduces students to the wonders of the natural world. Through hands-on experiments and observations, students learn about living things, matter, energy, and the environment.",
    objectives: [
      "Understand basic scientific concepts and processes",
      "Develop observation and inquiry skills",
      "Learn about living organisms and ecosystems",
      "Explore matter, energy, and physical phenomena",
      "Conduct simple experiments safely",
      "Apply scientific thinking to everyday situations"
    ],
    activities: [
      "Laboratory experiments",
      "Nature walks and observations",
      "Science fair projects",
      "Group investigations",
      "Video documentaries",
      "Environmental projects"
    ],
    materials: ["Science textbooks", "Lab equipment", "Microscopes", "Science kits", "Nature journals"],
    rating: 4.7,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#science-1" },
      { name: "Resource 2", url: "#science-2" },
      { name: "Resource 3", url: "#science-3" },
      { name: "Resource 4", url: "#science-4" },
      { name: "Resource 5", url: "#science-5" },
      { name: "Resource 6", url: "#science-6" },
      { name: "Resource 7", url: "#science-7" },
      { name: "Resource 8", url: "#science-8" },
      { name: "Resource 9", url: "#science-9" },
      { name: "Resource 10", url: "#science-10" },
      { name: "Homework 1", url: "#science-hw-1" },
      { name: "Homework 2", url: "#science-hw-2" },
      { name: "Homework 3", url: "#science-hw-3" },
      { name: "Homework 4", url: "#science-hw-4" },
      { name: "Homework 5", url: "#science-hw-5" },
      { name: "Homework 6", url: "#science-hw-6" },
      { name: "Homework 7", url: "#science-hw-7" },
      { name: "Homework 8", url: "#science-hw-8" },
      { name: "Homework 9", url: "#science-hw-9" },
      { name: "Homework 10", url: "#science-hw-10" },
    ]
  },
  rme: {
    name: "Religious and Moral Education (RME)",
    color: "from-blue-500 to-cyan-400",
    icon: "✝️",
    room: "Block B, Room 201",
    schedule: "Tue/Thu 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building moral character and understanding religious values for responsible citizenship.",
    fullDescription: "The RME program helps students develop strong moral values and understand religious teachings. Students learn about Christianity, Islam, and Traditional African Religion while developing ethical decision-making skills.",
    objectives: [
      "Understand major world religions",
      "Develop moral reasoning and ethical values",
      "Learn to respect different beliefs and cultures",
      "Apply religious teachings to daily life",
      "Build character through moral education",
      "Become responsible citizens"
    ],
    activities: [
      "Bible and religious text studies",
      "Moral dilemma discussions",
      "Community service projects",
      "Role-playing ethical scenarios",
      "Comparative religion studies",
      "Character development activities"
    ],
    materials: ["RME textbooks", "Holy books", "Moral story collections", "Visual aids", "Discussion guides"],
    rating: 4.8,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#rme-1" },
      { name: "Resource 2", url: "#rme-2" },
      { name: "Resource 3", url: "#rme-3" },
      { name: "Resource 4", url: "#rme-4" },
      { name: "Resource 5", url: "#rme-5" },
      { name: "Resource 6", url: "#rme-6" },
      { name: "Resource 7", url: "#rme-7" },
      { name: "Resource 8", url: "#rme-8" },
      { name: "Resource 9", url: "#rme-9" },
      { name: "Resource 10", url: "#rme-10" },
      { name: "Homework 1", url: "#rme-hw-1" },
      { name: "Homework 2", url: "#rme-hw-2" },
      { name: "Homework 3", url: "#rme-hw-3" },
      { name: "Homework 4", url: "#rme-hw-4" },
      { name: "Homework 5", url: "#rme-hw-5" },
      { name: "Homework 6", url: "#rme-hw-6" },
      { name: "Homework 7", url: "#rme-hw-7" },
      { name: "Homework 8", url: "#rme-hw-8" },
      { name: "Homework 9", url: "#rme-hw-9" },
      { name: "Homework 10", url: "#rme-hw-10" },
    ]
  },
  "creative-arts": {
    name: "Creative Arts",
    color: "from-blue-500 to-cyan-400",
    icon: "🎨",
    room: "Art Studio",
    schedule: "Tue/Thu 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Expressing creativity through visual arts, music, dance, and drama.",
    fullDescription: "The Creative Arts program nurtures students' artistic talents and creative expression. Students explore various art forms including drawing, painting, music, dance, and drama.",
    objectives: [
      "Develop artistic skills and techniques",
      "Express creativity through various media",
      "Appreciate different art forms and cultures",
      "Build confidence through performance",
      "Learn art history and appreciation",
      "Create original artworks and performances"
    ],
    activities: [
      "Drawing and painting sessions",
      "Music and singing lessons",
      "Dance and movement classes",
      "Drama performances",
      "Art exhibitions",
      "Cultural celebrations"
    ],
    materials: ["Art supplies", "Musical instruments", "Costumes", "Art portfolios", "Creative arts textbooks"],
    rating: 4.9,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#arts-1" },
      { name: "Resource 2", url: "#arts-2" },
      { name: "Resource 3", url: "#arts-3" },
      { name: "Resource 4", url: "#arts-4" },
      { name: "Resource 5", url: "#arts-5" },
      { name: "Resource 6", url: "#arts-6" },
      { name: "Resource 7", url: "#arts-7" },
      { name: "Resource 8", url: "#arts-8" },
      { name: "Resource 9", url: "#arts-9" },
      { name: "Resource 10", url: "#arts-10" },
      { name: "Homework 1", url: "#arts-hw-1" },
      { name: "Homework 2", url: "#arts-hw-2" },
      { name: "Homework 3", url: "#arts-hw-3" },
      { name: "Homework 4", url: "#arts-hw-4" },
      { name: "Homework 5", url: "#arts-hw-5" },
      { name: "Homework 6", url: "#arts-hw-6" },
      { name: "Homework 7", url: "#arts-hw-7" },
      { name: "Homework 8", url: "#arts-hw-8" },
      { name: "Homework 9", url: "#arts-hw-9" },
      { name: "Homework 10", url: "#arts-hw-10" },
    ]
  },
  history: {
    name: "History",
    color: "from-blue-500 to-cyan-400",
    icon: "📜",
    room: "Block B, Room 202",
    schedule: "Mon/Wed 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Understanding Ghana's past and world history to appreciate our heritage.",
    fullDescription: "The History program helps students understand Ghana's rich heritage and world civilizations. Students learn about important events, leaders, and cultural developments that shaped our world.",
    objectives: [
      "Learn about Ghana's history and independence",
      "Understand African civilizations and kingdoms",
      "Explore world history and major events",
      "Develop historical thinking skills",
      "Appreciate cultural heritage",
      "Connect past events to present situations"
    ],
    activities: [
      "Historical timelines and projects",
      "Research assignments",
      "Museum visits (virtual and physical)",
      "Documentary viewings",
      "Historical debates",
      "Heritage day celebrations"
    ],
    materials: ["History textbooks", "Maps and atlases", "Historical documents", "Visual aids", "Research materials"],
    rating: 4.6,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#history-1" },
      { name: "Resource 2", url: "#history-2" },
      { name: "Resource 3", url: "#history-3" },
      { name: "Resource 4", url: "#history-4" },
      { name: "Resource 5", url: "#history-5" },
      { name: "Resource 6", url: "#history-6" },
      { name: "Resource 7", url: "#history-7" },
      { name: "Resource 8", url: "#history-8" },
      { name: "Resource 9", url: "#history-9" },
      { name: "Resource 10", url: "#history-10" },
      { name: "Homework 1", url: "#history-hw-1" },
      { name: "Homework 2", url: "#history-hw-2" },
      { name: "Homework 3", url: "#history-hw-3" },
      { name: "Homework 4", url: "#history-hw-4" },
      { name: "Homework 5", url: "#history-hw-5" },
      { name: "Homework 6", url: "#history-hw-6" },
      { name: "Homework 7", url: "#history-hw-7" },
      { name: "Homework 8", url: "#history-hw-8" },
      { name: "Homework 9", url: "#history-hw-9" },
      { name: "Homework 10", url: "#history-hw-10" },
    ]
  },
  french: {
    name: "French",
    color: "from-blue-500 to-cyan-400",
    icon: "🇫🇷",
    room: "Language Lab",
    schedule: "Tue/Thu 9:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Learning French language and Francophone culture for global communication.",
    fullDescription: "The French program introduces students to the French language and Francophone cultures. Students develop listening, speaking, reading, and writing skills in French.",
    objectives: [
      "Learn basic French vocabulary and grammar",
      "Develop conversational French skills",
      "Read and write simple French texts",
      "Understand Francophone cultures",
      "Build foundation for advanced French study",
      "Appreciate language learning"
    ],
    activities: [
      "Conversational practice",
      "French songs and games",
      "Role-playing scenarios",
      "Cultural presentations",
      "Reading comprehension",
      "Writing exercises"
    ],
    materials: ["French textbooks", "Audio materials", "Flashcards", "French dictionaries", "Cultural resources"],
    rating: 4.7,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#french-1" },
      { name: "Resource 2", url: "#french-2" },
      { name: "Resource 3", url: "#french-3" },
      { name: "Resource 4", url: "#french-4" },
      { name: "Resource 5", url: "#french-5" },
      { name: "Resource 6", url: "#french-6" },
      { name: "Resource 7", url: "#french-7" },
      { name: "Resource 8", url: "#french-8" },
      { name: "Resource 9", url: "#french-9" },
      { name: "Resource 10", url: "#french-10" },
      { name: "Homework 1", url: "#french-hw-1" },
      { name: "Homework 2", url: "#french-hw-2" },
      { name: "Homework 3", url: "#french-hw-3" },
      { name: "Homework 4", url: "#french-hw-4" },
      { name: "Homework 5", url: "#french-hw-5" },
      { name: "Homework 6", url: "#french-hw-6" },
      { name: "Homework 7", url: "#french-hw-7" },
      { name: "Homework 8", url: "#french-hw-8" },
      { name: "Homework 9", url: "#french-hw-9" },
      { name: "Homework 10", url: "#french-hw-10" },
    ]
  },
  fante: {
    name: "Fante",
    color: "from-blue-500 to-cyan-400",
    icon: "🇬🇭",
    room: "Block B, Room 203",
    schedule: "Mon/Wed/Fri 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Preserving and promoting our local Fante language and cultural heritage.",
    fullDescription: "The Fante program helps students master their local language and appreciate their cultural heritage. Students develop fluency in speaking, reading, and writing Fante.",
    objectives: [
      "Develop fluency in spoken Fante",
      "Read and write Fante correctly",
      "Learn Fante proverbs and idioms",
      "Understand Fante cultural practices",
      "Appreciate local traditions and values",
      "Preserve linguistic heritage"
    ],
    activities: [
      "Conversational Fante practice",
      "Storytelling sessions",
      "Proverb interpretation",
      "Cultural performances",
      "Reading and writing exercises",
      "Traditional games"
    ],
    materials: ["Fante textbooks", "Storybooks", "Audio materials", "Cultural artifacts", "Writing materials"],
    rating: 4.8,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#fante-1" },
      { name: "Resource 2", url: "#fante-2" },
      { name: "Resource 3", url: "#fante-3" },
      { name: "Resource 4", url: "#fante-4" },
      { name: "Resource 5", url: "#fante-5" },
      { name: "Resource 6", url: "#fante-6" },
      { name: "Resource 7", url: "#fante-7" },
      { name: "Resource 8", url: "#fante-8" },
      { name: "Resource 9", url: "#fante-9" },
      { name: "Resource 10", url: "#fante-10" },
      { name: "Homework 1", url: "#fante-hw-1" },
      { name: "Homework 2", url: "#fante-hw-2" },
      { name: "Homework 3", url: "#fante-hw-3" },
      { name: "Homework 4", url: "#fante-hw-4" },
      { name: "Homework 5", url: "#fante-hw-5" },
      { name: "Homework 6", url: "#fante-hw-6" },
      { name: "Homework 7", url: "#fante-hw-7" },
      { name: "Homework 8", url: "#fante-hw-8" },
      { name: "Homework 9", url: "#fante-hw-9" },
      { name: "Homework 10", url: "#fante-hw-10" },
    ]
  },
  "computer-science": {
    name: "Computer Science, Coding and Engineering",
    color: "from-blue-500 to-cyan-400",
    icon: "💻",
    room: "ICT Lab",
    schedule: "Mon/Wed/Fri 1:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building digital literacy and computational thinking skills for the 21st century.",
    fullDescription: "The Computer Science program prepares students for the digital age. Students learn computer basics, coding fundamentals, and engineering concepts through hands-on projects.",
    objectives: [
      "Develop computer literacy and skills",
      "Learn basic programming concepts",
      "Understand how technology works",
      "Build problem-solving and logical thinking",
      "Create digital projects and presentations",
      "Practice safe and responsible technology use"
    ],
    activities: [
      "Computer skills practice",
      "Coding exercises and games",
      "Robotics projects",
      "Digital presentations",
      "Internet safety lessons",
      "Engineering challenges"
    ],
    materials: ["Computers", "Coding software", "Robotics kits", "ICT textbooks", "Digital resources"],
    rating: 4.9,
    enrolled: 120,
    resourceLinks: [
      { name: "Resource 1", url: "#cs-1" },
      { name: "Resource 2", url: "#cs-2" },
      { name: "Resource 3", url: "#cs-3" },
      { name: "Resource 4", url: "#cs-4" },
      { name: "Resource 5", url: "#cs-5" },
      { name: "Resource 6", url: "#cs-6" },
      { name: "Resource 7", url: "#cs-7" },
      { name: "Resource 8", url: "#cs-8" },
      { name: "Resource 9", url: "#cs-9" },
      { name: "Resource 10", url: "#cs-10" },
      { name: "Homework 1", url: "#cs-hw-1" },
      { name: "Homework 2", url: "#cs-hw-2" },
      { name: "Homework 3", url: "#cs-hw-3" },
      { name: "Homework 4", url: "#cs-hw-4" },
      { name: "Homework 5", url: "#cs-hw-5" },
      { name: "Homework 6", url: "#cs-hw-6" },
      { name: "Homework 7", url: "#cs-hw-7" },
      { name: "Homework 8", url: "#cs-hw-8" },
      { name: "Homework 9", url: "#cs-hw-9" },
      { name: "Homework 10", url: "#cs-hw-10" },
    ]
  }
};

// Basic School Subject Page Component
export const BasicSchoolSubjectPage = () => {
  const navigate = useNavigate();
  const { subject } = useParams();
  
  const subjectData = basicSchoolSubjectsData[subject];
  
  if (!subjectData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-400 to-cyan-400 flex items-center justify-center">
        <Card className="bg-white p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-[#002147] mb-4">Subject Not Found</h2>
          <p className="text-gray-600 mb-6">The subject you're looking for doesn't exist.</p>
          <Button 
            onClick={() => navigate("/course-catalog")}
            className="bg-[#FF6B00] hover:bg-[#E65C00]"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to Course Catalog
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-400 to-cyan-400" data-testid={`basic-subject-${subject}`}>
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${subjectData.color} text-white py-12`}>
        <div className="max-w-5xl mx-auto px-4">
          <Button 
            className="mb-4 bg-gradient-to-b from-white/30 to-white/10 hover:from-white/40 hover:to-white/20 text-white border-2 border-white/50 transition-all rounded-full shadow-[0_4px_0_0_rgba(0,0,0,0.3)] hover:shadow-[0_2px_0_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium"
            onClick={() => navigate("/course-catalog")}
            data-testid="back-to-courses-btn"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to Course Catalog
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
              {subjectData.icon}
            </div>
            <div>
              <Badge className="bg-white/20 text-white mb-2">Basic School</Badge>
              <h1 className="text-3xl md:text-4xl font-bold">{subjectData.name}</h1>
              <p className="text-white/90 mt-2 text-lg">{subjectData.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resource & Homework Buttons Section */}
      {subjectData.resourceLinks && subjectData.resourceLinks.length > 0 && (
        <div className="bg-gradient-to-r from-pink-400 via-pink-300 to-pink-400 py-6">
          <div className="max-w-6xl mx-auto px-4">
            {/* Row 1: Resource buttons */}
            <div className="flex flex-wrap justify-center gap-2 mb-3">
              {subjectData.resourceLinks.filter(r => r.name.startsWith('Resource')).map((resource, index) => {
                const buttonColors = [
                  { bg: 'linear-gradient(145deg, #ff6b6b, #ee5a24)', shadow: '#c0392b' },
                  { bg: 'linear-gradient(145deg, #ffa502, #e67e22)', shadow: '#d35400' },
                  { bg: 'linear-gradient(145deg, #7bed9f, #2ed573)', shadow: '#27ae60' },
                  { bg: 'linear-gradient(145deg, #70a1ff, #1e90ff)', shadow: '#0c5bb5' },
                  { bg: 'linear-gradient(145deg, #a29bfe, #6c5ce7)', shadow: '#4834d4' },
                  { bg: 'linear-gradient(145deg, #fd79a8, #e84393)', shadow: '#b83280' },
                  { bg: 'linear-gradient(145deg, #fdcb6e, #f39c12)', shadow: '#d68910' },
                  { bg: 'linear-gradient(145deg, #55efc4, #00b894)', shadow: '#00856a' },
                  { bg: 'linear-gradient(145deg, #74b9ff, #0984e3)', shadow: '#0767b1' },
                  { bg: 'linear-gradient(145deg, #dfe6e9, #b2bec3)', shadow: '#808e95' },
                ];
                const colorSet = buttonColors[index % buttonColors.length];
                const isPlaceholder = resource.url.startsWith('#');
                return (
                  <a
                    key={index}
                    href={isPlaceholder ? undefined : resource.url}
                    target={isPlaceholder ? undefined : "_blank"}
                    rel={isPlaceholder ? undefined : "noopener noreferrer"}
                    onClick={isPlaceholder ? (e) => e.preventDefault() : undefined}
                    className="inline-block px-4 py-2 text-xs font-bold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 active:translate-y-[3px] active:shadow-none no-underline"
                    style={{
                      background: colorSet.bg,
                      boxShadow: `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`,
                      transform: 'translateY(-2px)',
                    }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = `0 2px 0 ${colorSet.shadow}, 0 3px 5px rgba(0,0,0,0.15)`; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    data-testid={`resource-btn-${index + 1}`}
                  >
                    {resource.name}
                  </a>
                );
              })}
            </div>
            {/* Row 2: Homework buttons */}
            <div className="flex flex-wrap justify-center gap-2">
              {subjectData.resourceLinks.filter(r => r.name.startsWith('Homework')).map((resource, index) => {
                const buttonColors = [
                  { bg: 'linear-gradient(145deg, #ff6b6b, #ee5a24)', shadow: '#c0392b' },
                  { bg: 'linear-gradient(145deg, #ffa502, #e67e22)', shadow: '#d35400' },
                  { bg: 'linear-gradient(145deg, #7bed9f, #2ed573)', shadow: '#27ae60' },
                  { bg: 'linear-gradient(145deg, #70a1ff, #1e90ff)', shadow: '#0c5bb5' },
                  { bg: 'linear-gradient(145deg, #a29bfe, #6c5ce7)', shadow: '#4834d4' },
                  { bg: 'linear-gradient(145deg, #fd79a8, #e84393)', shadow: '#b83280' },
                  { bg: 'linear-gradient(145deg, #fdcb6e, #f39c12)', shadow: '#d68910' },
                  { bg: 'linear-gradient(145deg, #55efc4, #00b894)', shadow: '#00856a' },
                  { bg: 'linear-gradient(145deg, #74b9ff, #0984e3)', shadow: '#0767b1' },
                  { bg: 'linear-gradient(145deg, #dfe6e9, #b2bec3)', shadow: '#808e95' },
                ];
                const colorSet = buttonColors[index % buttonColors.length];
                const isPlaceholder = resource.url.startsWith('#');
                return (
                  <a
                    key={index}
                    href={isPlaceholder ? undefined : resource.url}
                    target={isPlaceholder ? undefined : "_blank"}
                    rel={isPlaceholder ? undefined : "noopener noreferrer"}
                    onClick={isPlaceholder ? (e) => e.preventDefault() : undefined}
                    className="inline-block px-4 py-2 text-xs font-bold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 active:translate-y-[3px] active:shadow-none no-underline"
                    style={{
                      background: colorSet.bg,
                      boxShadow: `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`,
                      transform: 'translateY(-2px)',
                    }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = `0 2px 0 ${colorSet.shadow}, 0 3px 5px rgba(0,0,0,0.15)`; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    data-testid={`homework-btn-${index + 1}`}
                  >
                    {resource.name}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <BookOpen size={20} /> About This Course
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">{subjectData.fullDescription}</p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Target size={20} /> Learning Objectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {subjectData.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{objective}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Lightbulb size={20} /> Learning Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {subjectData.activities.map((activity, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-700 text-sm">{activity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-[#002147] text-white rounded-t-lg">
                <CardTitle className="text-lg">Course Instructor</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="text-center">
                  {subjectData.teacherImage ? (
                    <img 
                      src={subjectData.teacherImage} 
                      alt={subjectData.teacher}
                      className="w-24 h-24 rounded-full mx-auto border-4 border-blue-500 object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full mx-auto border-4 border-blue-200 bg-blue-50 flex items-center justify-center">
                      <UserCircle size={40} className="text-blue-300" />
                    </div>
                  )}
                  <h3 className="font-semibold text-[#002147] mt-3">{subjectData.teacher}</h3>
                  <div className="flex items-center justify-center gap-1 text-yellow-500 mt-1">
                    <Star size={14} fill="currentColor" />
                    <span className="text-sm font-medium">{subjectData.rating} rating</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg text-[#002147]">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Schedule</p>
                    <p className="font-medium text-gray-800">{subjectData.schedule}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <MapPin size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-medium text-gray-800">{subjectData.room}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Enrolled</p>
                    <p className="font-medium text-gray-800">{subjectData.enrolled} Students</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <GraduationCap size={18} className="text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level</p>
                    <p className="font-medium text-gray-800">Basic School</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button 
              className="w-full bg-blue-500 hover:bg-blue-600"
              onClick={() => navigate("/portal")}
            >
              <User size={16} className="mr-2" /> Contact Teacher
            </Button>
          </div>
        </div>
        
        {/* Bottom Back Button */}
        <div className="mt-8 text-center">
          <Button 
            className="bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium px-8"
            onClick={() => navigate("/course-catalog")}
            data-testid="back-to-courses-btn-bottom"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to Course Catalog
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BasicSchoolSubjectPage;
